import {
  InputFieldsType,
  designTypeFields,
  orderedFields,
} from '@composites/InboundButton/utils';
import {
  PublicExhibitionItem,
  PublicSessionItem,
} from '@hubs101/booking-api-skd-client/lib/types';

import { groupAndSortByDate, safeJSONParse, uniqBy, getUrl } from '@utils/fn';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ACCOUNT_ID,
  ROOT_URL,
  useGetPublicEventAdvertisement,
  useGetPublicExhibitionGroupsByCategory,
  usePublicAllAgendas,
  usePublicCoLocatedEventsByEventId,
  usePublicEventDetailsBySlug,
  usePublicExhibitions,
  usePublicPortfolioByAccount,
} from '@utils/apiHooks';
import { useSelector } from 'react-redux';

import AgendaPdfContent from '@composites/PDF/AgendaPdfContent';
import Button from '@components/Button';
import { DefaultBooleanValues } from '@composites/InboundButton';

import { agendaPdfDays } from '@pages/Agenda/helper';
import { getEventDetails } from '@store/slices/eventSlice';
import { getUniquePdfDays } from '@utils/time';
import { printPdf, useMobileWidth } from '@utils/hooks';
import { useForm } from 'react-hook-form';
import { useInbound } from '@utils/Inbound/useInbound';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Ticket } from '@composites/Tickets';
import Spinner from '@components/Spinner';
import dayjs from 'dayjs';

interface DownloadAgendaProps {
  label: string;
  buttonType: string;
  backgroundColor?: string;
  eventSlug?: string;
  inboundValues?: {
    nonBooleanValues: Record<string, string>;
    booleanValues: DefaultBooleanValues;
  };
  width?: string;
}
const DownloadAgenda = ({
  label,
  buttonType,
  backgroundColor,
  inboundValues,
  eventSlug,
  width,
}: DownloadAgendaProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const eventDetailsBySlug = usePublicEventDetailsBySlug({
    eventSlug: eventSlug ?? '', // safe fallback
    enabled: !!eventSlug,
  });

  const event = eventSlug
    ? eventDetailsBySlug?.data?.[0] || sluggedEvent
    : sluggedEvent;

  const { shouldShowForm, inboundValue, actionInbound, registerInbound } =
    useInbound();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [pdfGenerationLoading, setPdfGenerationLoading] =
    useState<boolean>(false);
  const isMobile = useMobileWidth();

  const [forceEnable, setForceEnable] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const getSessionEventId = (s: any): string | undefined => {
    const e = s?.event;
    if (!e) return undefined;
    if (typeof e === 'string') return e;
    return e.id || e.event_id || e.eventId;
  };

  const { data: portfolios } = usePublicPortfolioByAccount({
    accountId: ACCOUNT_ID,
  });

  const {
    isSuccess,
    data: coLocatedEvents,
    isLoading: isCoLocatedEventsLoading,
  } = usePublicCoLocatedEventsByEventId({
    eventId: event?.id,
    enabled: event?.id?.length > 0 && event?.colocated,
  });

  const { data: advertisements = [] } = useGetPublicEventAdvertisement({
    eventId: event?.id || '',
  });

  const agendas: any =
    usePublicAllAgendas({
      page: 0,
      language: event?.language,
      eventId: event?.id,
      enabled: event?.id?.length > 0,
      colocated: false,
    }) || [];
  const agendasSessions = useMemo(() => {
    const raw = agendas?.data;
    if (Array.isArray(raw)) {
      return raw
        .map((e: any) => e?.sessions)
        .flat()
        .filter(Boolean) as PublicSessionItem[];
    }
    return (raw?.sessions ?? []) as PublicSessionItem[];
  }, [agendas?.data]);

  const correctedSessions = useMemo(() => {
    if (!Array.isArray(agendasSessions) || agendasSessions.length === 0) {
      return agendasSessions;
    }

    const start = dayjs(sluggedEvent?.start_date).tz(sluggedEvent?.timezone);
    const end = dayjs(sluggedEvent?.end_date).tz(sluggedEvent?.timezone);
    if (!start.isValid() || !end.isValid()) return agendasSessions;

    const eventDays: string[] = [];
    for (
      let d = start.startOf('day');
      d.isBefore(end, 'day') || d.isSame(end, 'day');
      d = d.add(1, 'day')
    ) {
      eventDays.push(d.format('YYYY-MM-DD'));
    }
    const k = eventDays.length;
    if (!k) return agendasSessions;

    const toYMD = (iso?: string) =>
      dayjs(iso).tz(sluggedEvent?.timezone).format('YYYY-MM-DD');

    const srcDaysByEvent = new Map<string, string[]>();
    for (const s of agendasSessions) {
      const ev = getSessionEventId(s) ?? sluggedEvent?.id ?? 'unknown';
      const key = toYMD(s.start_date);
      if (!srcDaysByEvent.has(ev)) srcDaysByEvent.set(ev, []);
      const arr = srcDaysByEvent.get(ev)!;
      if (!arr.includes(key)) arr.push(key);
    }

    for (const [ev, arr] of srcDaysByEvent) {
      arr.sort(); // ASC
      if (arr.length > k) srcDaysByEvent.set(ev, arr.slice(-k));
    }

    const mapPerEvent = new Map<string, Map<string, string>>();
    for (const [ev, arr] of srcDaysByEvent) {
      const m = new Map<string, string>();
      for (let i = 0; i < arr.length && i < k; i++) {
        m.set(arr[i], eventDays[i]);
      }
      mapPerEvent.set(ev, m);
    }

    const nearestIndex = (arr: string[], src: string) => {
      if (!arr.length) return 0;
      let bestIdx = 0;
      let best = Math.abs(dayjs(src).diff(dayjs(arr[0]), 'day'));
      for (let i = 1; i < arr.length; i++) {
        const d = Math.abs(dayjs(src).diff(dayjs(arr[i]), 'day'));
        if (d < best) {
          best = d;
          bestIdx = i;
        }
      }
      return bestIdx;
    };

    const mapped = agendasSessions.map(s => {
      const ev = getSessionEventId(s) ?? sluggedEvent?.id ?? 'unknown';
      const srcKey = toYMD(s.start_date);

      const perEvent = mapPerEvent.get(ev);
      const arr = srcDaysByEvent.get(ev) ?? [];
      let targetDay = perEvent?.get(srcKey);

      if (!targetDay) {
        const idx = nearestIndex(arr, srcKey);
        targetDay = eventDays[Math.min(idx, k - 1)] ?? eventDays[0];
      }

      const sStart = dayjs(s.start_date);
      const durationMin = dayjs(s.end_date).diff(sStart, 'minute');

      const correctedStart = dayjs(targetDay)
        .hour(sStart.hour())
        .minute(sStart.minute())
        .second(sStart.second())
        .millisecond(sStart.millisecond());

      return {
        ...s,
        start_date: correctedStart.toISOString(),
        end_date: correctedStart.add(durationMin, 'minute').toISOString(),
      } as PublicSessionItem;
    });

    // --- Sorting ---
    mapped.sort((a, b) => {
      const dateA = dayjs(a.start_date);
      const dateB = dayjs(b.start_date);
      if (dateA.isBefore(dateB)) return -1;
      if (dateA.isAfter(dateB)) return 1;

      // If start_date is equal, sort by priority (lower first)
      const priorityA = typeof a.priority === 'number' ? a.priority : 9999;
      const priorityB = typeof b.priority === 'number' ? b.priority : 9999;
      return priorityA - priorityB;
    });

    return mapped;
  }, [agendasSessions, sluggedEvent?.start_date, sluggedEvent?.end_date]);

  // current-data
  const sessionsForPdf = useMemo(() => {
    const currentEventId = sluggedEvent?.id;
    if (!currentEventId) return correctedSessions;

    const mine = correctedSessions.filter(
      s => getSessionEventId(s) === currentEventId
    );

    //  "previous-data"
    return mine.length > 0 ? mine : correctedSessions;
  }, [correctedSessions, sluggedEvent?.id]);

  const { data: eliteGroups = [], isLoading: isEliteLoading } =
    useGetPublicExhibitionGroupsByCategory({
      eventId: sluggedEvent?.id,
      category: 'partner_elite',
      enabled: !!sluggedEvent?.id,
    });
  const { data: eventLeadGroups = [], isLoading: isEventLeadLoading } =
    useGetPublicExhibitionGroupsByCategory({
      eventId: sluggedEvent?.id,
      category: 'partner_eventlead',
      enabled: !!sluggedEvent?.id,
    });
  const { data: competenceGroups = [], isLoading: isCompetenceLoading } =
    useGetPublicExhibitionGroupsByCategory({
      eventId: sluggedEvent?.id,
      category: 'partner_competence',
      enabled: !!sluggedEvent?.id,
    });
  const { data: businessGroups = [], isLoading: isBusinessLoading } =
    useGetPublicExhibitionGroupsByCategory({
      eventId: sluggedEvent?.id,
      category: 'partner_business',
      enabled: !!sluggedEvent?.id,
    });

  const isExhibitionLoading =
    isEventLeadLoading ||
    isEliteLoading ||
    isCompetenceLoading ||
    isBusinessLoading;

  const exhibitions = useMemo(() => {
    // Sort each group by order ascending
    const sortedElite = [...eliteGroups].sort(
      (a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0)
    );
    const sortedEventLead = [...eventLeadGroups].sort(
      (a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0)
    );
    const sortedCompetence = [...competenceGroups].sort(
      (a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0)
    );
    const sortedBusiness = [...businessGroups].sort(
      (a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0)
    );

    // Merge in the required order
    const merged = [
      ...sortedElite,
      ...sortedEventLead,
      ...sortedCompetence,
      ...sortedBusiness,
    ];

    // Remove duplicates by id
    const seen = new Set<string>();
    const unique = merged.filter((x: any) => {
      const key = String(x?.id ?? '');
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return unique;
  }, [eliteGroups, eventLeadGroups, competenceGroups, businessGroups]);

  const agendaPdfDates = useMemo(() => {
    const agendaDaysPdfFormat = getUniquePdfDays(
      sessionsForPdf?.map(e => e?.start_date) ?? []
    );
    const result = agendaDaysPdfFormat?.map((e, index) => {
      return agendaDaysPdfFormat?.length === 1
        ? agendaDaysPdfFormat[index]
        : `${t(agendaPdfDays[index])} - ${agendaDaysPdfFormat[index]}`;
    });
    return result;
  }, [sessionsForPdf, t]);

  const groupedAgendasSessions = useMemo(
    () => Object.values(groupAndSortByDate(sessionsForPdf, 'start_date')),
    [sessionsForPdf]
  );

  const agendasSpeakers = useMemo(
    () =>
      uniqBy(
        sessionsForPdf
          ?.map(e => e?.speakers)
          ?.flat()
          ?.filter(Boolean),
        'profile'
      ),
    [sessionsForPdf]
  );

  const generateAgendaPDF = useCallback(() => {
    const eventName = eventSlug
      ? coLocatedEvents?.find(e => e?.slug === eventSlug)?.name || ''
      : event?.name;

    const doc = (
      <AgendaPdfContent
        event={event}
        coLocatedEvents={coLocatedEvents}
        portfolios={portfolios}
        theme={theme}
        groupedAgendasSessions={groupedAgendasSessions}
        agendaPdfDates={agendaPdfDates}
        agendaSpeakers={agendasSpeakers}
        exhibitions={exhibitions}
        tickets={tickets}
        advertisements={advertisements}
      />
    );
    setPdfGenerationLoading(true);
    printPdf(doc, `${t('agenda')} - ${eventName}`)
      .catch(console.error)
      .finally(() => {
        setPdfGenerationLoading(false);
      });
  }, [
    agendaPdfDates,
    agendasSpeakers,
    exhibitions,
    groupedAgendasSessions,
    event,
    t,
    theme,
  ]);

  useEffect(() => {
    if (event?.id) {
      fetch(`${ROOT_URL}/tickets?event=${event?.id}`)
        .then(async response => await response.json())
        .then(data => data.result.data)
        .then(ticketsData => {
          const relevantTickets = ticketsData
            .filter(
              (ticket: Ticket) =>
                ticket.visibility === 'public' && ticket.attendee_type === 'de'
            )
            .sort((a: Ticket, b: Ticket) => a.priority - b.priority);

          setTickets(relevantTickets);
        })
        .catch(err => {
          console.error('error = ', err);
        });
    }
  }, [event]);

  useEffect(() => {
    if (!forceEnable) {
      timerRef.current = setTimeout(() => {
        setForceEnable(true);
      }, 5000);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [forceEnable]);

  const isReady =
    (!!event &&
      !!agendas?.data &&
      Array.isArray(exhibitions) &&
      !pdfGenerationLoading &&
      !isCoLocatedEventsLoading &&
      !agendas?.isLoading &&
      !isExhibitionLoading) ||
    (forceEnable && !pdfGenerationLoading);

  return (
    <>
      <Button
        title={label}
        icon={pdfGenerationLoading ? <Spinner size="2rem" /> : undefined}
        backgroundColor={backgroundColor || event?.design?.themeColor}
        hoverBackgroundColor={theme.color.mobilityGrey}
        textColor={theme.color.white}
        lineHeight={theme.lineHeight.height125}
        fontSize={theme.fontSize.size20}
        borderRadius={theme.borderRadius.radius10}
        padding="1.1rem 3rem"
        width={isMobile ? '100%' : width || 'fit-content'}
        disabled={!isReady}
        onClick={() => {
          void (async () => {
            if (shouldShowForm) {
              // REDIRECT
              const base = `${window.location.origin}/events/${event?.slug}`;
              const target = `${base}/${getUrl(t('eventguide-update-url'))}`;
              window.location.href = target;
              return;
            }

            // Has inbound value => download + inbound
            generateAgendaPDF();

            await actionInbound({
              ...JSON.parse(inboundValue ?? '{}'),
              event: event?.id,
              path: window.location.href,
              action: 'download_agenda',
            });
          })();
        }}
      />
    </>
  );
};

export default DownloadAgenda;
